import { ReactNode, useLayoutEffect, useRef, useState } from "react";

// A horizontal drag must clear this many px, and stay this much more horizontal
// than vertical, before it counts as a page-turn swipe (so vertical scrolling of
// a tall page and diagonal drags are never hijacked).
const SWIPE_MIN_X = 60;
const SWIPE_MAX_Y_RATIO = 0.7;

// Readable measure cap and the minimum horizontal inset on each side of the
// column. On wide screens the column is centred at MAX_COL with large side
// margins; on narrow screens it fills the width minus MIN_SIDE on each edge.
const MAX_COL = 672;
const MIN_SIDE = 20;

export interface PageLayout {
  /** Number of pages the current chapter fills. */
  pageCount: number;
  /**
   * Page index where each block begins, keyed by PagerBlock.key. A block that
   * spills across a column boundary reports the page it *starts* on, so a long
   * paragraph split over two pages maps to the first of them.
   */
  pageOfKey: Record<string, number>;
}

export interface PagerBlock {
  key: string;
  node: ReactNode;
}

interface PagerProps {
  blocks: PagerBlock[];
  /** Zero-based page to display. Clamped to the available range. */
  page: number;
  /** Fired after (re)measurement with the resulting page layout. */
  onLayout: (layout: PageLayout) => void;
  /**
   * Typography/spacing classes for the text column (font, size, leading,
   * vertical padding, inter-block spacing). The column width and horizontal
   * insets are owned by the Pager and set imperatively — do NOT include
   * `flex`, a max-width, or horizontal padding here, as they fight the
   * multi-column layout.
   */
  contentClassName: string;
  /** Swipe left (e.g. turn to the next page). Touch only. */
  onSwipeLeft?: () => void;
  /** Swipe right (e.g. turn to the previous page). Touch only. */
  onSwipeRight?: () => void;
  /**
   * Returns true when the gesture just ended was a text selection (drag), so
   * the swipe should be ignored — the reader suppresses native selection and
   * drives its own, so a selection drag can otherwise look like a page swipe.
   */
  suppressSwipe?: () => boolean;
}

/**
 * Multi-column paginator. Lays the whole chapter out in a fixed-height CSS
 * multi-column flow whose column width equals one page, so the browser packs
 * the text into viewport-height columns and breaks long paragraphs at line
 * boundaries (never mid-line) when they outgrow a page. Turning a page just
 * translates the flow horizontally by one viewport width.
 *
 * Geometry, with viewport width W and column width colW:
 *   side   = (W - colW) / 2            (equal inset each side of the column)
 *   gap    = 2 * side                  (column-gap)
 *   stride = colW + gap == W           (one viewport width per page)
 * Because the flow carries `side` padding-left and a `2*side` gap, each column
 * lands inset by `side` on both edges of its W-wide page slot once translated.
 */
export function Pager({
  blocks,
  page,
  onLayout,
  contentClassName,
  onSwipeLeft,
  onSwipeRight,
  suppressSwipe,
}: PagerProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [geom, setGeom] = useState({ stride: 0, count: 1 });
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  // Keep the latest onLayout without retriggering the measurement effect.
  const onLayoutRef = useRef(onLayout);
  onLayoutRef.current = onLayout;

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const compute = () => {
      const W = viewport.clientWidth;
      const H = viewport.clientHeight;
      if (W <= 0 || H <= 0) return;

      const colW = Math.min(MAX_COL, W - 2 * MIN_SIDE);
      const side = Math.max(MIN_SIDE, (W - colW) / 2);
      const stride = colW + 2 * side; // == W

      // Apply the column geometry imperatively so React (which only owns the
      // transform/transition) never clobbers it on re-render.
      content.style.columnWidth = `${colW}px`;
      content.style.columnGap = `${2 * side}px`;
      content.style.columnFill = "auto";
      content.style.paddingLeft = `${side}px`;
      content.style.paddingRight = `${side}px`;
      content.style.orphans = "1";
      content.style.widows = "1";

      // Reading scrollWidth forces the reflow, so the styles above are live.
      const count = Math.max(
        1,
        Math.round((content.scrollWidth + 2 * side) / stride),
      );

      const pageOfKey: Record<string, number> = {};
      for (const child of Array.from(content.children) as HTMLElement[]) {
        const key = child.dataset.key;
        if (key == null) continue;
        pageOfKey[key] = Math.min(
          count - 1,
          Math.max(0, Math.round(child.offsetLeft / stride)),
        );
      }

      setGeom((prev) =>
        prev.stride === stride && prev.count === count
          ? prev
          : { stride, count },
      );
      onLayoutRef.current({ pageCount: count, pageOfKey });
    };

    let cancelled = false;
    const recompute = () => {
      if (!cancelled) compute();
    };
    compute();
    // Re-measure on viewport resize (window/orientation, mobile browser chrome)
    // and on content reflow — notably the serif web-font swap, which grows line
    // heights without changing the viewport, so a viewport-only observer would
    // leave the page count computed against the fallback font.
    const ro = new ResizeObserver(recompute);
    ro.observe(viewport);
    ro.observe(content);
    void document.fonts?.ready.then(recompute);
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [blocks]);

  const safePage = Math.max(0, Math.min(page, geom.count - 1));

  // Slide only on single-step turns (swipe / next / prev). Multi-page jumps —
  // deep-link restore, jumping to a chapter's last page — snap without a whoosh.
  const lastPage = useRef(safePage);
  const animate = Math.abs(safePage - lastPage.current) === 1;
  useLayoutEffect(() => {
    lastPage.current = safePage;
  });

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      touchStart.current = null;
      return;
    }
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const from = touchStart.current;
    touchStart.current = null;
    if (!from) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - from.x;
    const dy = t.clientY - from.y;
    // Ignore short drags, mostly-vertical drags, and any drag that ends a text
    // selection (the selection is the user's intent).
    if (Math.abs(dx) < SWIPE_MIN_X) return;
    if (Math.abs(dy) > Math.abs(dx) * SWIPE_MAX_Y_RATIO) return;
    if (suppressSwipe?.()) return;
    if (dx < 0) onSwipeLeft?.();
    else onSwipeRight?.();
  };

  return (
    <div
      ref={viewportRef}
      className="relative flex-1 overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* The whole chapter as one fixed-height multi-column flow; only the
          column(s) for the current page sit inside the clipped viewport. */}
      <div
        ref={contentRef}
        className={`relative h-full ${contentClassName}`}
        style={{
          transform: `translateX(-${safePage * geom.stride}px)`,
          transition: animate ? "transform 220ms ease" : "none",
        }}
      >
        {blocks.map((b) => (
          <div key={b.key} data-key={b.key}>
            {b.node}
          </div>
        ))}
      </div>
    </div>
  );
}
